export const tabs = [
  "overview",
  "templates",
  "messages",
  "usage",
  "billing",
] as const;
export type WhatsappTab = (typeof tabs)[number];
export function readTab(params: URLSearchParams): WhatsappTab {
  const value = params.get("tab");
  return tabs.find((tab) => tab === value) ?? "overview";
}
export function dateDefaults(now = new Date(), days: 1 | 7 | 30 | 60 | 90 = 30) {
  const dateTo = now.toISOString().slice(0, 10);
  const start = new Date(`${dateTo}T00:00:00Z`);
  start.setUTCDate(start.getUTCDate() - days + 1);
  return { dateFrom: start.toISOString().slice(0, 10), dateTo };
}
export function readDates(params: URLSearchParams) {
  const defaults = dateDefaults();
  return {
    dateFrom: params.get("dateFrom") ?? defaults.dateFrom,
    dateTo: params.get("dateTo") ?? defaults.dateTo,
  };
}
export type MessageSource = "trioty" | "meta";
export function readMessageSource(params: URLSearchParams): MessageSource {
  return params.get("source") === "meta" ? "meta" : "trioty";
}
const ledgerFields = [
  "page",
  "sellerId",
  "storeId",
  "unattributed",
  "purpose",
  "template",
  "status",
  "acceptance",
  "messageId",
];
export function queryFor(tab: WhatsappTab, params: URLSearchParams) {
  const ledger =
    tab === "usage" ||
    (tab === "messages" && readMessageSource(params) === "trioty");
  const query = new URLSearchParams(
    ledger || tab === "overview" || tab === "billing"
      ? readDates(params)
      : undefined,
  );
  const fields =
    tab === "templates"
      ? ["after", "name", "templateStatus", "category", "language"]
      : ledger
        ? [...ledgerFields, ...(tab === "usage" ? ["groupBy", "search"] : [])]
        : tab === "messages"
          ? ["after", "messageId", "dateFrom", "dateTo", "status"]
          : [];
  for (const field of fields) {
    const value = params.get(field);
    if (value) query.set(field === "templateStatus" ? "status" : field, value);
  }
  if (tab === "messages" || tab === "templates" || tab === "usage")
    query.set("limit", "20");
  return query.toString();
}
export function changeMessageSource(
  current: URLSearchParams,
  source: MessageSource,
) {
  // Keep only filters with the same meaning in both sources; dates use different clocks.
  const next = new URLSearchParams({ tab: "messages", source });
  const messageId = current.get("messageId");
  if (messageId) next.set("messageId", messageId);
  return next;
}
export function changeParams(
  current: URLSearchParams,
  changes: Record<string, string | null>,
  resetPage = true,
) {
  const next = new URLSearchParams(current);
  if (resetPage) {
    next.delete("page");
    next.delete("after");
    next.delete("cursorTrail");
  }
  for (const [key, value] of Object.entries(changes)) {
    if (value) next.set(key, value);
    else next.delete(key);
  }
  return next;
}
export function datesValid(from: string, to: string) {
  const start = Date.parse(`${from}T00:00:00Z`);
  const end = Date.parse(`${to}T00:00:00Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/.test(from) &&
    /^\d{4}-\d{2}-\d{2}$/.test(to) &&
    Number.isFinite(start) &&
    Number.isFinite(end) &&
    new Date(start).toISOString().slice(0, 10) === from &&
    new Date(end).toISOString().slice(0, 10) === to &&
    end >= start &&
    end - start < 90 * 86400000
  );
}
